import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * localStorage key holding the array of notification ids the customer has opened.
 * Read state deliberately outlives the browsing session, so a notification that
 * has been read stays read on the next visit.
 */
const STORAGE_KEY = 'theme:notifications-read';

/**
 * Reads the set of already-opened notification ids.
 *
 * Storage access throws in Safari private browsing and when the customer has
 * blocked site data, so every read/write is guarded — an unavailable store just
 * means everything reads as unread.
 *
 * @returns {Set<string>} The ids of notifications that have been opened.
 */
function readOpenedIds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];

    return new Set(Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

/**
 * Persists the set of opened notification ids.
 *
 * @param {Iterable<string>} ids - The ids to store.
 */
function writeOpenedIds(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // Storage unavailable — read state is best-effort only.
  }
}

/**
 * Fires a GA4 event through the theme's analytics helper.
 *
 * `window.track` comes from the deferred `analytics.js`, which this module can
 * evaluate ahead of — hence the guard. Every call here is user-initiated, so in
 * practice the helper is always present by the time one fires. The CTA click is
 * tracked declaratively from the markup instead, via `data-ga-event`.
 *
 * @param {string} name - The event name.
 * @param {Record<string, unknown>} params - The event parameters.
 */
function track(name, params) {
  window.track?.(name, params);
}

/**
 * A custom element that manages the notifications panel.
 *
 * The panel has two views, swapped via the dialog's `data-view` attribute: the
 * list of notifications, and the detail for one of them. Opening a detail marks
 * that notification read, which clears its unread marker and decrements the
 * count badge on the header trigger.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} body - The scrolling region holding both views.
 * @property {HTMLElement[]} items - The notification rows in the list view.
 * @property {HTMLElement[]} details - The detail panels, index-aligned with `items`.
 *
 * @extends {DialogComponent}
 */
class NotificationsComponent extends DialogComponent {
  requiredRefs = ['dialog', 'body', 'items', 'details'];

  /**
   * Index of the row that opened the current detail view, so focus can be
   * returned to it on the way back.
   * @type {number | null}
   */
  #openedFrom = null;

  /**
   * Unread count as of the last sync, reported with the panel-open event.
   * @type {number}
   */
  #unreadCount = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#syncUnreadState();
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose);
    window.addEventListener('storage', this.#handleStorage);
  }

  /**
   * A morph re-renders the rows from the server, wiping the unread markers and
   * badge text this component wrote, so reapply them.
   */
  updatedCallback() {
    super.updatedCallback();

    this.#syncUnreadState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(DialogCloseEvent.eventName, this.#handleClose);
    window.removeEventListener('storage', this.#handleStorage);
  }

  /** The notification ids currently rendered, in display order. @returns {string[]} */
  get #ids() {
    return this.refs.items.map((item) => item.dataset.notificationId ?? '');
  }

  /**
   * Opens the panel on the list view, from the header trigger, and reports the
   * open to GA4.
   *
   * `source` is still reported even though the header is now the only trigger —
   * it keeps the event shape stable for reporting built while the menu drawer
   * footer also had one.
   */
  open = () => {
    this.#showList(null);
    this.showDialog();

    track('buff_notification_open', {
      source: 'header',
      unread_count: this.#unreadCount,
      notification_count: this.refs.items.length,
    });
  };

  /**
   * Shows the detail view for a notification and marks it read.
   *
   * @param {number} index - The index of the notification to show.
   */
  showDetail = (index) => {
    const position = Number(index);
    const item = this.refs.items[position];
    const detail = this.refs.details[position];

    if (!item || !detail) return;

    const id = item.dataset.notificationId;

    // Read before marking — opening is what clears the unread state, so this is
    // the only point at which we can tell a first read from a re-read.
    track('buff_notification_view', {
      notification_id: id,
      notification_title: item.querySelector('.notifications__item-title')?.textContent?.trim(),
      notification_index: position + 1,
      was_unread: item.hasAttribute('data-unread') ? 'true' : 'false',
    });

    if (id) this.#markOpened(id);

    // Direction first: the CSS picks its animation off this attribute, and the
    // reveal below is what starts the animation.
    this.refs.dialog.dataset.direction = 'forward';

    for (const panel of this.refs.details) panel.hidden = panel !== detail;

    this.#openedFrom = position;
    this.refs.dialog.dataset.view = 'detail';
    this.refs.body.scrollTop = 0;

    // Focus the detail heading so screen readers and keyboard users land on the
    // new view rather than staying on the now-hidden row.
    detail.querySelector('[data-notification-detail-heading]')?.focus();
  };

  /**
   * Returns from the detail view to the list.
   */
  back = () => {
    const returnTo = this.#openedFrom;

    this.#showList('back');

    if (returnTo != null) {
      const trigger = this.refs.items[returnTo]?.querySelector('button');

      if (trigger instanceof HTMLElement) trigger.focus();
    }
  };

  /**
   * Resets the panel to the list view.
   *
   * @param {'back' | null} direction - 'back' when returning from a detail, so
   *   the list pops in as one piece; null on a fresh open, where the rows
   *   stagger in instead.
   */
  #showList(direction) {
    const { dialog } = this.refs;

    if (direction) dialog.dataset.direction = direction;
    else delete dialog.dataset.direction;

    for (const panel of this.refs.details) panel.hidden = true;

    this.#openedFrom = null;
    dialog.dataset.view = 'list';
    this.refs.body.scrollTop = 0;
  }

  /**
   * Closing the panel resets it, so reopening always starts on the list —
   * including the opening stagger, hence clearing the direction.
   */
  #handleClose = () => {
    this.#showList(null);
  };

  /**
   * Another tab opened a notification — refresh the dots and badges here too.
   *
   * @param {StorageEvent} event - The storage event.
   */
  #handleStorage = (event) => {
    if (event.key !== STORAGE_KEY) return;

    this.#syncUnreadState();
  };

  /**
   * Records a notification as opened.
   *
   * @param {string} id - The notification id.
   */
  #markOpened(id) {
    const opened = readOpenedIds();

    if (opened.has(id)) return;

    opened.add(id);

    // Drop ids for notifications the merchant has since removed, so the stored
    // entry can't grow without bound.
    const present = new Set(this.#ids);
    writeOpenedIds([...opened].filter((value) => present.has(value)));

    this.#syncUnreadState();
  }

  /**
   * Marks unread rows and updates every count badge in the document.
   */
  #syncUnreadState() {
    const opened = readOpenedIds();
    let unread = 0;

    for (const item of this.refs.items) {
      const id = item.dataset.notificationId;
      const isUnread = Boolean(id) && !opened.has(/** @type {string} */ (id));

      item.toggleAttribute('data-unread', isUnread);
      if (isUnread) unread += 1;
    }

    for (const badge of document.querySelectorAll('[data-notifications-badge]')) {
      badge.textContent = unread > 99 ? '99+' : String(unread);
      badge.toggleAttribute('hidden', unread === 0);
    }

    for (const count of document.querySelectorAll('[data-notifications-unread-count]')) {
      count.textContent = String(unread);
    }

    this.#unreadCount = unread;
  }
}

if (!customElements.get('notifications-component')) {
  customElements.define('notifications-component', NotificationsComponent);
}
